import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Mail, Inbox, RefreshCw, CheckCircle2, AlertTriangle, Loader2,
  Send, Eye, EyeOff, Save, Trash2, Zap,
} from 'lucide-react'
import api from '@/lib/api'

interface SmtpStatus {
  configured: boolean
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_username?: string | null
  smtp_from_name?: string | null
  signature_html?: string | null
  signature_image_url?: string | null
}
interface ImapStatus {
  imap_host?: string | null
  imap_port?: number | null
  imap_username?: string | null
  imap_enabled?: boolean | null
}
interface CrmSyncStatus {
  enabled: boolean
  imap_configured: boolean
  smtp_configured: boolean
  first_enabled_at?: string | null
  sync_status?: 'idle' | 'queued' | 'running' | 'completed' | 'error'
  sync_progress_total?: number
  sync_progress_done?: number
  last_poll_at?: string | null
  last_error?: string | null
  sync_triggered?: boolean
}

type Msg = { kind: 'success' | 'error'; text: string } | null

export default function EmailIntegrationsTab() {
  // ---- SMTP state ----
  const [smtpStatus, setSmtpStatus] = useState<SmtpStatus | null>(null)
  const [smtpForm, setSmtpForm] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_from_name: '', signature_html: '' })
  const [smtpShowPw, setSmtpShowPw] = useState(false)
  const [smtpEdit, setSmtpEdit] = useState(false)
  const [smtpSaving, setSmtpSaving] = useState(false)
  const [smtpTesting, setSmtpTesting] = useState(false)
  const [smtpMsg, setSmtpMsg] = useState<Msg>(null)
  const [sigUploading, setSigUploading] = useState(false)

  // ---- IMAP state ----
  const [imapStatus, setImapStatus] = useState<ImapStatus | null>(null)
  const [imapForm, setImapForm] = useState({ imap_host: 'imap.gmail.com', imap_port: 993, imap_username: '', imap_password: '', imap_enabled: true })
  const [imapShowPw, setImapShowPw] = useState(false)
  const [imapEdit, setImapEdit] = useState(false)
  const [imapSaving, setImapSaving] = useState(false)
  const [imapTesting, setImapTesting] = useState(false)
  const [imapMsg, setImapMsg] = useState<Msg>(null)

  // ---- CRM sync state ----
  const [crmSync, setCrmSync] = useState<CrmSyncStatus | null>(null)
  const [crmSyncSaving, setCrmSyncSaving] = useState(false)
  const [crmMsg, setCrmMsg] = useState<Msg>(null)
  const [emailsCapturedSnapshot, setEmailsCapturedSnapshot] = useState<number | null>(null)

  // ---- Loaders ----
  async function loadAll() {
    try {
      const [smtp, outreach, sync] = await Promise.all([
        api.get<SmtpStatus>('/v1/users/me/smtp'),
        api.get<{ imap: ImapStatus }>('/v1/outreach/config'),
        api.get<CrmSyncStatus>('/v1/users/me/crm-email-sync'),
      ])
      setSmtpStatus(smtp.data)
      setSmtpForm(f => ({
        ...f,
        smtp_host: smtp.data.smtp_host || f.smtp_host,
        smtp_port: smtp.data.smtp_port || 587,
        smtp_username: smtp.data.smtp_username || '',
        smtp_from_name: smtp.data.smtp_from_name || '',
        signature_html: smtp.data.signature_html || '',
      }))
      setImapStatus(outreach.data.imap || {})
      setImapForm(f => ({
        ...f,
        imap_host: outreach.data.imap?.imap_host || f.imap_host,
        imap_port: outreach.data.imap?.imap_port || 993,
        imap_username: outreach.data.imap?.imap_username || '',
        imap_enabled: outreach.data.imap?.imap_enabled ?? true,
      }))
      setCrmSync(sync.data)
    } catch (e) {
      // silent — show fields anyway
    }
  }

  useEffect(() => { loadAll() }, [])

  // ---- SMTP actions ----
  async function saveSmtp() {
    setSmtpSaving(true); setSmtpMsg(null)
    try {
      const { data } = await api.put<SmtpStatus>('/v1/users/me/smtp', smtpForm)
      setSmtpStatus(data); setSmtpEdit(false)
      setSmtpForm(f => ({ ...f, smtp_password: '' }))
      setSmtpMsg({ kind: 'success', text: 'SMTP salvo. Use "Testar" para validar.' })
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao salvar SMTP' })
    } finally { setSmtpSaving(false) }
  }
  async function testSmtp() {
    setSmtpTesting(true); setSmtpMsg(null)
    try {
      const { data } = await api.post<{ success?: boolean; ok?: boolean; message: string }>('/v1/users/me/smtp/test')
      const ok = data.success ?? data.ok ?? false
      setSmtpMsg({ kind: ok ? 'success' : 'error', text: data.message })
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao testar SMTP' })
    } finally { setSmtpTesting(false) }
  }
  async function deleteSmtp() {
    if (!confirm('Remover configuração SMTP?')) return
    try {
      await api.delete('/v1/users/me/smtp')
      await loadAll()
      setSmtpMsg({ kind: 'success', text: 'SMTP removido.' })
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao remover SMTP' })
    }
  }

  // ---- IMAP actions ----
  async function saveImap() {
    setImapSaving(true); setImapMsg(null)
    try {
      await api.put('/v1/outreach/config/imap', imapForm)
      await loadAll()
      setImapEdit(false)
      setImapForm(f => ({ ...f, imap_password: '' }))
      setImapMsg({ kind: 'success', text: 'IMAP salvo. Use "Testar" para validar.' })
    } catch (e: any) {
      setImapMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao salvar IMAP' })
    } finally { setImapSaving(false) }
  }
  async function testImap() {
    setImapTesting(true); setImapMsg(null)
    try {
      const { data } = await api.post<{ success?: boolean; ok?: boolean; message?: string; error?: string; mailbox_count?: number }>('/v1/outreach/config/imap/test')
      const ok = data.success ?? data.ok ?? false
      const extras = data.mailbox_count != null ? ` (${data.mailbox_count} mailboxes)` : ''
      const text = (data.message || data.error || (ok ? 'Conexão OK' : 'Falha na conexão')) + extras
      setImapMsg({ kind: ok ? 'success' : 'error', text })
    } catch (e: any) {
      setImapMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao testar IMAP' })
    } finally { setImapTesting(false) }
  }
  async function deleteImap() {
    if (!confirm('Remover configuração IMAP?')) return
    try {
      await api.delete('/v1/outreach/config/imap')
      await loadAll()
      setImapMsg({ kind: 'success', text: 'IMAP removido.' })
    } catch (e: any) {
      setImapMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao remover IMAP' })
    }
  }

  // ---- CRM sync toggle ----
  async function toggleCrmSync(next: boolean) {
    setCrmSyncSaving(true); setCrmMsg(null)
    try {
      const { data } = await api.put<CrmSyncStatus>('/v1/users/me/crm-email-sync', { enabled: next })
      setCrmSync(data)
      if (next) {
        setEmailsCapturedSnapshot(null)
        setCrmMsg({ kind: 'success', text: data.sync_triggered ? 'Sincronização iniciada. Analisando seus e-mails dos últimos 120 dias…' : 'Sincronização ativada.' })
      } else {
        setCrmMsg({ kind: 'success', text: 'Sincronização CRM desativada.' })
      }
    } catch (e: any) {
      setCrmMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao alterar sincronização' })
    } finally { setCrmSyncSaving(false) }
  }

  // ---- Progress polling: refresh status every 2s while sync is queued/running ----
  useEffect(() => {
    if (!crmSync) return
    const active = crmSync.sync_status === 'queued' || crmSync.sync_status === 'running'
    if (!active) return
    const t = setInterval(async () => {
      try {
        const { data } = await api.get<CrmSyncStatus>('/v1/users/me/crm-email-sync')
        setCrmSync(data)
        if (data.sync_status === 'completed' && emailsCapturedSnapshot == null) {
          setEmailsCapturedSnapshot(data.sync_progress_done || 0)
          setCrmMsg({ kind: 'success', text: `Sincronização concluída. ${data.sync_progress_total} mensagens analisadas.` })
        }
        if (data.sync_status === 'error') {
          setCrmMsg({ kind: 'error', text: data.last_error || 'Erro na sincronização.' })
        }
      } catch {
        // silent — keep trying
      }
    }, 2000)
    return () => clearInterval(t)
  }, [crmSync?.sync_status])

  // ---- helpers ----
  const StatusPill = ({ ok, label }: { ok: boolean; label?: string }) =>
    ok ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-3 w-3" /> {label || 'Conectado'}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400">
        <AlertTriangle className="h-3 w-3" /> {label || 'Não configurado'}
      </span>
    )

  const MsgBlock = ({ msg }: { msg: Msg }) =>
    msg ? (
      <div className={`text-xs rounded p-2 ${msg.kind === 'success' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-destructive/10 text-destructive'}`}>
        {msg.text}
      </div>
    ) : null

  const smtpOk = !!smtpStatus?.configured
  const imapOk = !!(imapStatus?.imap_host && imapStatus?.imap_username)
  const syncEnabled = !!crmSync?.enabled

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Integrações de E-mail</h2>
        <p className="text-sm text-muted-foreground">SMTP para envio, IMAP para monitoramento e Sincronização CRM em um único lugar.</p>
      </div>

      {/* ============== 1. SMTP ============== */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            <div>
              <div className="font-semibold">SMTP — Envio de e-mails</div>
              <div className="text-xs text-muted-foreground">Servidor para envio (Gmail, Outlook, etc).</div>
            </div>
          </div>
          <StatusPill ok={smtpOk} />
        </div>
        <p className="text-xs text-muted-foreground">
          Configure suas credenciais para que propostas comerciais e e-mails do CRM sejam enviados a partir do seu próprio e-mail. Para Gmail, gere um <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary underline">App Password</a> (requer 2FA na conta Google).
        </p>

        {smtpStatus && !smtpEdit && smtpOk && (
          <div className="text-sm text-muted-foreground space-y-0.5">
            <div><span className="text-xs uppercase tracking-wide">Servidor:</span> <span className="font-mono">{smtpStatus.smtp_host}:{smtpStatus.smtp_port}</span></div>
            <div><span className="text-xs uppercase tracking-wide">Usuário:</span> {smtpStatus.smtp_username}</div>
            {smtpStatus.smtp_from_name && <div><span className="text-xs uppercase tracking-wide">Nome:</span> {smtpStatus.smtp_from_name}</div>}
          </div>
        )}

        {smtpEdit && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <Input placeholder="Servidor SMTP" value={smtpForm.smtp_host} onChange={e => setSmtpForm(f => ({ ...f, smtp_host: e.target.value }))} />
            <Input type="number" placeholder="Porta" value={smtpForm.smtp_port} onChange={e => setSmtpForm(f => ({ ...f, smtp_port: parseInt(e.target.value || '587', 10) }))} />
            <Input placeholder="Usuário (e-mail)" value={smtpForm.smtp_username} onChange={e => setSmtpForm(f => ({ ...f, smtp_username: e.target.value }))} />
            <div className="relative">
              <Input type={smtpShowPw ? 'text' : 'password'} placeholder="Senha ou App Password" value={smtpForm.smtp_password} onChange={e => setSmtpForm(f => ({ ...f, smtp_password: e.target.value }))} />
              <button type="button" onClick={() => setSmtpShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                {smtpShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Input className="sm:col-span-2" placeholder="Nome do remetente (ex: Vitor Bragantini — NAI)" value={smtpForm.smtp_from_name} onChange={e => setSmtpForm(f => ({ ...f, smtp_from_name: e.target.value }))} />
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Assinatura — Texto HTML (opcional, aparece em cima)</label>
              <textarea
                value={smtpForm.signature_html}
                onChange={e => setSmtpForm(f => ({ ...f, signature_html: e.target.value }))}
                rows={4}
                className="w-full mt-1 px-3 py-2 text-xs bg-background border rounded-md font-mono"
                placeholder='Atenciosamente,'
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Texto opcional acima da imagem. Variáveis Jinja: {`{{ nome }}, {{ empresa }}, {{ email }}`}.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Assinatura — Imagem (opcional, aparece abaixo)</label>
              {smtpStatus?.signature_image_url ? (
                <div className="mt-1 flex items-center gap-3 p-3 border rounded-md bg-background">
                  <img src={smtpStatus.signature_image_url} alt="Assinatura" style={{ maxWidth: 240, maxHeight: 120, border: 0 }} />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="outline" isLoading={sigUploading} onClick={() => document.getElementById('sig-file-input')?.click()} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Trocar imagem</Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      if (!confirm('Remover imagem da assinatura?')) return
                      try {
                        const { data } = await api.delete<SmtpStatus>('/v1/users/me/smtp/signature-image')
                        setSmtpStatus(data)
                        setSmtpMsg({ kind: 'success', text: 'Imagem removida.' })
                      } catch (e: any) {
                        setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao remover' })
                      }
                    }} leftIcon={<Trash2 className="h-3.5 w-3.5" />}>Remover</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-1">
                  <Button size="sm" variant="outline" isLoading={sigUploading} onClick={() => document.getElementById('sig-file-input')?.click()} leftIcon={<Mail className="h-3.5 w-3.5" />}>Anexar imagem</Button>
                </div>
              )}
              <input
                id="sig-file-input"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  if (file.size > 2 * 1024 * 1024) {
                    setSmtpMsg({ kind: 'error', text: `Imagem excede 2MB (${Math.round(file.size / 1024)}KB).` })
                    e.target.value = ''
                    return
                  }
                  setSigUploading(true); setSmtpMsg(null)
                  try {
                    const form = new FormData()
                    form.append('file', file)
                    const { data } = await api.post<SmtpStatus>('/v1/users/me/smtp/signature-image', form, { headers: { 'Content-Type': 'multipart/form-data' } })
                    setSmtpStatus(data)
                    setSmtpMsg({ kind: 'success', text: 'Imagem anexada. Lembre de clicar "Sincronizar com SMTP de Configurações" em Email Marketing → Contas pra aplicar nas campanhas.' })
                  } catch (err: any) {
                    setSmtpMsg({ kind: 'error', text: err?.response?.data?.detail || 'Falha no upload da imagem' })
                  } finally {
                    setSigUploading(false)
                    e.target.value = ''
                  }
                }}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                PNG, JPG, GIF, WebP ou SVG até 2MB. A imagem fica hospedada em <code>social.myncc.com.br/portfolio/signatures/</code>. Use o botão <b>"Sincronizar com SMTP de Configurações"</b> em Email Marketing → Contas para aplicar nas contas de campanha.
              </p>
            </div>
          </div>
        )}

        <MsgBlock msg={smtpMsg} />

        <div className="flex flex-wrap gap-2 pt-1">
          {!smtpEdit ? (
            <>
              <Button size="sm" onClick={() => setSmtpEdit(true)}>{smtpOk ? 'Editar' : 'Configurar'}</Button>
              {smtpOk && (
                <Button size="sm" variant="outline" onClick={testSmtp} isLoading={smtpTesting} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Testar</Button>
              )}
              {smtpOk && <Button size="sm" variant="outline" onClick={deleteSmtp} leftIcon={<Trash2 className="h-3.5 w-3.5" />}>Remover</Button>}
            </>
          ) : (
            <>
              <Button size="sm" onClick={saveSmtp} isLoading={smtpSaving} leftIcon={<Save className="h-3.5 w-3.5" />}>Salvar</Button>
              <Button size="sm" variant="outline" onClick={() => { setSmtpEdit(false); setSmtpMsg(null) }}>Cancelar</Button>
            </>
          )}
        </div>
      </Card>

      {/* ============== 2. IMAP ============== */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" />
            <div>
              <div className="font-semibold">IMAP — Monitoramento da caixa de entrada</div>
              <div className="text-xs text-muted-foreground">Leitura read-only para detectar notificações LinkedIn e e-mails comerciais.</div>
            </div>
          </div>
          <StatusPill ok={imapOk} />
        </div>
        <p className="text-xs text-muted-foreground">
          Conexão somente leitura à sua caixa de entrada para sincronizar conversas comerciais com as oportunidades. Para Gmail, use o mesmo <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary underline">App Password</a> do SMTP (requer 2FA na conta Google).
        </p>

        {imapStatus && !imapEdit && imapOk && (
          <div className="text-sm text-muted-foreground space-y-0.5">
            <div><span className="text-xs uppercase tracking-wide">Servidor:</span> <span className="font-mono">{imapStatus.imap_host}:{imapStatus.imap_port}</span></div>
            <div><span className="text-xs uppercase tracking-wide">Usuário:</span> {imapStatus.imap_username}</div>
          </div>
        )}

        {imapEdit && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <Input placeholder="Servidor IMAP" value={imapForm.imap_host} onChange={e => setImapForm(f => ({ ...f, imap_host: e.target.value }))} />
            <Input type="number" placeholder="Porta" value={imapForm.imap_port} onChange={e => setImapForm(f => ({ ...f, imap_port: parseInt(e.target.value || '993', 10) }))} />
            <Input placeholder="Usuário (e-mail)" value={imapForm.imap_username} onChange={e => setImapForm(f => ({ ...f, imap_username: e.target.value }))} />
            <div className="relative">
              <Input type={imapShowPw ? 'text' : 'password'} placeholder="Senha ou App Password" value={imapForm.imap_password} onChange={e => setImapForm(f => ({ ...f, imap_password: e.target.value }))} />
              <button type="button" onClick={() => setImapShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                {imapShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        <MsgBlock msg={imapMsg} />

        <div className="flex flex-wrap gap-2 pt-1">
          {!imapEdit ? (
            <>
              <Button size="sm" onClick={() => setImapEdit(true)}>{imapOk ? 'Editar' : 'Configurar'}</Button>
              {imapOk && (
                <Button size="sm" variant="outline" onClick={testImap} isLoading={imapTesting} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Testar</Button>
              )}
              {imapOk && <Button size="sm" variant="outline" onClick={deleteImap} leftIcon={<Trash2 className="h-3.5 w-3.5" />}>Remover</Button>}
            </>
          ) : (
            <>
              <Button size="sm" onClick={saveImap} isLoading={imapSaving} leftIcon={<Save className="h-3.5 w-3.5" />}>Salvar</Button>
              <Button size="sm" variant="outline" onClick={() => { setImapEdit(false); setImapMsg(null) }}>Cancelar</Button>
            </>
          )}
        </div>
      </Card>

      {/* ============== 3. CRM Email Sync ============== */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <div>
              <div className="font-semibold">Sincronização automática com Oportunidades</div>
              <div className="text-xs text-muted-foreground">Detecta e-mails enviados/recebidos de contatos cadastrados e anexa à timeline da oportunidade correspondente.</div>
            </div>
          </div>
          <StatusPill ok={syncEnabled} label={syncEnabled ? 'Ativa' : 'Inativa'} />
        </div>

        {crmSync && !crmSync.imap_configured && (
          <div className="text-xs rounded p-2 bg-amber-500/10 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Configure o IMAP acima antes de ativar a sincronização CRM.
          </div>
        )}

        <ul className="text-sm text-muted-foreground space-y-1 ml-1">
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Identifica contatos do CRM via FROM/TO/CC do e-mail</li>
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Vincula automaticamente à oportunidade aberta do contato (regra: opp aberta mais recente)</li>
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Threading via Message-ID/In-Reply-To/References (RFC 5322)</li>
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Captura também e-mails enviados (pasta Sent do Gmail) — funciona com qualquer cliente (Gmail web, Outlook, etc.)</li>
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Filtra newsletters/automações automaticamente (List-Unsubscribe, Auto-Submitted, no-reply, domínios marketing)</li>
        </ul>

        <MsgBlock msg={crmMsg} />

        <div className="flex items-center gap-3 pt-1">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={syncEnabled}
              disabled={crmSyncSaving || !crmSync?.imap_configured}
              onChange={(e) => toggleCrmSync(e.target.checked)}
            />
            <div className="relative w-11 h-6 bg-muted peer-checked:bg-primary rounded-full transition-colors peer-disabled:opacity-50">
              <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-background rounded-full shadow transition-transform ${syncEnabled ? 'translate-x-5' : ''}`} />
            </div>
            <span className="ml-3 text-sm">{syncEnabled ? 'Sincronização ativa' : 'Ativar sincronização'}</span>
          </label>
          {crmSyncSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Progress bar — active during initial historical sync */}
        {crmSync && (crmSync.sync_status === 'queued' || crmSync.sync_status === 'running') && (
          <div className="mt-2 p-3 rounded-md border bg-muted/30 space-y-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="font-medium">
                  {crmSync.sync_status === 'queued' ? 'Iniciando análise…' : 'Analisando e-mails dos últimos 120 dias'}
                </span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">
                {crmSync.sync_progress_done || 0} / {crmSync.sync_progress_total || '?'}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${(crmSync.sync_progress_total && crmSync.sync_progress_total > 0) ? Math.min(100, Math.round((crmSync.sync_progress_done || 0) / crmSync.sync_progress_total * 100)) : 5}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              A primeira sincronização cobre 120 dias retroativos a partir do momento da ativação. Atualizações seguintes acontecem a cada 5 minutos automaticamente.
            </p>
          </div>
        )}

        {crmSync && crmSync.sync_status === 'completed' && emailsCapturedSnapshot != null && (
          <div className="mt-2 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-sm">
            <CheckCircle2 className="h-4 w-4 inline mr-1" />
            Histórico sincronizado: {crmSync.sync_progress_total} mensagens analisadas. Novos e-mails aparecerão automaticamente.
          </div>
        )}
      </Card>
    </div>
  )
}
